export interface FirmProfile {
  firm_name: string;
  address: string;
  phone: string;
  email: string;
  attorney_name: string;
  attorney_title: string;
  bar_number: string;
  signature_block: string;
}

export interface FirmProfileResponse extends FirmProfile {
  scope: 'company' | 'user';
  can_edit: boolean;
  /** Short-lived signed URL of the letterhead logo, or null (ZLAYT-1428). */
  logo_url?: string | null;
}

/** Mirrors LOGO_MAX_BYTES / LOGO_FORMATS on the backend (ZLAYT-1428). */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const LOGO_MIME_TYPES = ['image/png', 'image/jpeg'];

/**
 * Why a chosen logo file would be refused, or null when it can be uploaded.
 * The backend checks the real image format again; this only saves a round
 * trip for the obvious cases.
 */
export function logoFileProblem(file: File): string | null {
  if (!LOGO_MIME_TYPES.includes(file.type)) {
    return 'The logo must be a PNG or JPEG.';
  }
  if (file.size > LOGO_MAX_BYTES) return 'The logo must be 2 MB or smaller.';
  return null;
}

export const EMPTY_FIRM_PROFILE: FirmProfile = {
  firm_name: '',
  address: '',
  phone: '',
  email: '',
  attorney_name: '',
  attorney_title: 'Attorney at Law',
  bar_number: '',
  signature_block: '',
};

const nonBlankLines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

/**
 * The signature lines the demand letter will print for this profile.
 * Mirrors document_version.html on the backend (ZLAYT-1410).
 */
export function signaturePreviewLines(profile: FirmProfile): string[] {
  const custom = nonBlankLines(profile.signature_block || '');
  if (custom.length) return custom;

  const name = (profile.attorney_name || '').trim();
  if (!name) return ['[Attorney Name]', 'Attorney at Law'];

  const lines = [name];
  const title = (profile.attorney_title || '').trim();
  const bar = (profile.bar_number || '').trim();
  const firm = (profile.firm_name || '').trim();
  if (title) lines.push(title);
  if (bar) lines.push(`Bar No. ${bar}`);
  if (firm) lines.push(firm);
  return lines;
}

/**
 * The text letterhead lines the demand letter prints above the date, or null
 * when it prints none. Mirrors firm.has_letterhead and document_version.html
 * on the backend: the firm name (when set), the address lines, then a
 * phone | email line. A nameless letterhead still prints. The logo, when
 * there is one, prints above these lines and is shown separately.
 */
export function letterheadPreviewLines(profile: FirmProfile): string[] | null {
  const firm = (profile.firm_name || '').trim();
  const lines = [
    ...(firm ? [firm] : []),
    ...nonBlankLines(profile.address || ''),
  ];
  const contact = [profile.phone, profile.email]
    .map((value) => (value || '').trim())
    .filter(Boolean);
  if (contact.length) lines.push(contact.join(' | '));
  return lines.length ? lines : null;
}

const nonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Message to show for a failed firm-profile request.
 *
 * The shared axios instance rejects with the response body itself, so the
 * value here is one of: the DRF exception-handler envelope
 * ({ error: { message, details? } }, e.g. a serializer 400), the view's own
 * 403 ({ message }), a plain DRF { detail }, a raw field error dict, or the
 * 'Wrong Services' string the interceptor substitutes when there is no body.
 */
export function firmProfileErrorMessage(
  err: unknown,
  fallback: string
): string {
  const body: any =
    err && typeof err === 'object' && 'response' in (err as any)
      ? (err as any).response?.data
      : err;
  if (!body || typeof body !== 'object') return fallback;

  const envelope = body.error;
  if (envelope && typeof envelope === 'object') {
    if (Array.isArray(envelope.details) && envelope.details.length) {
      return envelope.details.join(' ');
    }
    if (nonEmptyString(envelope.message)) return envelope.message;
  }
  if (nonEmptyString(body.message)) return body.message;
  if (nonEmptyString(body.detail)) return body.detail;
  if (nonEmptyString(envelope)) return envelope;

  const fieldErrors = Object.entries(body)
    .filter(([, value]) => Array.isArray(value) || nonEmptyString(value))
    .map(([field, value]) => {
      const messages = Array.isArray(value) ? value : [value];
      return `${field}: ${messages.map(String).join(' ')}`;
    });
  return fieldErrors.length ? fieldErrors.join(' ') : fallback;
}
